import {
  Activity,
  AlertTriangle,
  Bot,
  Calendar,
  CalendarPlus,
  CheckCircle,
  Copy,
  DollarSign,
  Edit3,
  ExternalLink,
  Eye,
  Flame,
  Heart,
  Image as ImageIcon,
  Linkedin,
  Loader2,
  Maximize2,
  MessageCircle,
  MoreVertical,
  MousePointer2,
  RefreshCw,
  Send,
  Share2,
  Sparkles,
  Trash2,
  Video as VideoIcon,
  XCircle,
} from "lucide-react";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { useDropdownPosition } from "../../hooks/useDropdownPosition";
import { showToastSuccess } from "../../shared/alertService";
import { buildApiUrl } from "../../shared/constants";
import { cn } from "../../utils/cn";
import { getPlatformConfig, getPlatformName } from "../../utils/platformIcons";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Tooltip from "../ui/Tooltip";

// Rótulos dos gatilhos emocionais (mesmas chaves de EMOTIONAL_TRIGGERS em
// ai/tools/content_generator.py) — usado pra exibir qual gatilho gerou o hook
// do post, quando disponível (Creative AI).
const EMOTIONAL_TRIGGER_LABELS: Record<string, string> = {
  fomo: "FOMO",
  curiosidade: "Curiosidade",
  controversia: "Controvérsia",
  identidade: "Identidade",
  autoridade: "Autoridade",
  prova_social: "Prova Social",
  urgencia: "Urgência",
};

interface Post {
  id: number;
  content: string;
  platform?: string;
  status: string;
  agent_generated?: boolean;
  hashtags?: string[];
  emotional_trigger?: string | null;
  call_to_action?: string;
  call_to_action_url?: string;
  utm_url?: string;
  image_info?: {
    filename?: string;
    public_url?: string;
    [key: string]: unknown;
  };
  video_info?: {
    filename?: string;
    public_url?: string;
    [key: string]: unknown;
  };
  linkedin_url?: string;
  linkedin_post_id?: string;
  facebook_post_id?: string;
  instagram_post_id?: string;
  facebook_url?: string;
  instagram_url?: string;
  platform_response?: string;
  scheduled_date?: string;
  published_at?: string;
  failure_reason?: string;
  rejection_reason?: string;
  media_gallery?: Array<{
    id?: string;
    url?: string;
    public_url?: string;
    type?: "image" | "video";
    [key: string]: any;
  }>;
}

export interface PostMetrics {
  leads_count?: number;
  conversions?: number;
  spend?: number;
  sales?: number;
  impressions?: number;
  clicks?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  engagement_rate?: number;
  reach?: number;
}

interface PostCardProps {
  post: Post;
  postMetrics?: PostMetrics;
  onApprove?: () => void;
  onReject?: () => void;
  onRevertToPending?: () => void;
  onEdit?: () => void;
  onPreview?: () => void;
  onSchedule?: () => void;
  onUnschedule?: () => void;
  onDelete?: () => void;
  onUploadImage?: () => void;
  onUploadVideo?: () => void;
  onGenerateVideo?: () => void;
  onEditVideo?: () => void;
  onClearVideo?: () => void;
  onClearImage?: () => void;
  onPublishMulti?: () => void;
  onPublish?: () => void;
  onRepublish?: () => void;
  onReschedule?: () => void;
  // Ação para turbinar post com Meta Ads
  onBoostMetaAds?: () => void;
  onBoostLinkedInAds?: () => void;
  onSyncMetrics?: () => void;
  onGenerateCreative?: () => void;
  onEnhance?: () => void;
  onViewVariations?: (variations: any, originalUrl: string) => void;
  loadingStates?: {
    approving?: boolean;
    rejecting?: boolean;
    editing?: boolean;
    deleting?: boolean;
    scheduling?: boolean;
    uploadingImage?: boolean;
    publishing?: boolean;
    enhancing?: boolean;
    republishing?: boolean;
    generatingVideo?: boolean;
  };
  highlighted?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
  children?: React.ReactNode;
}

interface MenuAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: "default" | "destructive" | "warning";
  category?: "edit" | "media" | "ai" | "publish" | "schedule" | "danger";
  loading?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  onApprove,
  onReject,
  onRevertToPending,
  onEdit,
  onPreview,
  onSchedule,
  onUnschedule,
  onDelete,
  onUploadImage,
  onUploadVideo,
  onEditVideo,
  onClearVideo,
  onClearImage,
  onPublishMulti,
  onPublish,
  onRepublish,
  onReschedule,
  onBoostMetaAds,
  onBoostLinkedInAds,
  onSyncMetrics,
  onGenerateCreative,
  onViewVariations,
  postMetrics,
  loadingStates = {},
  highlighted = false,
  innerRef,
  children,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRejectionExpanded, setIsRejectionExpanded] = useState(false);
  const [rejectionIsClamped, setRejectionIsClamped] = useState(false);
  const rejectionTextRef = useRef<HTMLParagraphElement>(null);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [utmCopied, setUtmCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const nextMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (post.media_gallery && post.media_gallery.length > 0) {
      setCurrentMediaIndex((prev) => (prev + 1) % post.media_gallery!.length);
    }
  };

  const prevMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (post.media_gallery && post.media_gallery.length > 0) {
      setCurrentMediaIndex(
        (prev) =>
          (prev - 1 + post.media_gallery!.length) % post.media_gallery!.length,
      );
    }
  };

  // Posicionamento inteligente do dropdown
  const position = useDropdownPosition({
    isOpen: isMenuOpen,
    buttonRef: buttonRef as RefObject<HTMLElement>,
    menuRef: menuRef as RefObject<HTMLElement>,
    preferredPosition: "bottom-right",
    offset: 4,
  });

  // Detecta se a mensagem de rejeição foi de fato cortada pelo line-clamp-2
  // (contagem de caracteres não prevê o número de linhas de forma confiável).
  // Medido só no estado colapsado (onde o clamp está ativo) — não reavaliar
  // ao expandir, senão o botão "Ver menos" desapareceria.
  useEffect(() => {
    if (isRejectionExpanded) return;
    const el = rejectionTextRef.current;
    if (el) setRejectionIsClamped(el.scrollHeight > el.clientHeight);
  }, [post.rejection_reason, post.failure_reason, isRejectionExpanded]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return t("Não definido");

    try {
      const date = new Date(dateString);
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return t("Data inválida");
    }
  };

  // Truncar conteúdo para preview inline (removendo quebras de linha no preview para evitar pular linhas)
  const maxContentLength = 85; // Reduzido para garantir o "Ver mais" inline na 3ª linha
  const shouldTruncate = post.content && post.content.length > maxContentLength;
  const previewContent = post.content ? post.content.replace(/\n+/g, " ") : "";

  const handleCardClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    // Suporte para teclado
    if ("key" in e && e.key !== "Enter" && e.key !== " ") {
      return;
    }

    // Não abrir preview se clicar em botões ou menu
    if (
      (e.target as HTMLElement).closest("button") ||
      (e.target as HTMLElement).closest("a") ||
      (e.target as HTMLElement).closest('[role="menu"]')
    ) {
      return;
    }

    if (onPreview) {
      onPreview();
    }
  };

  // Organizar ações do menu por categoria
  const getMenuActions = (): MenuAction[] => {
    const actions: MenuAction[] = [];

    // Categoria: Edição
    actions.push({
      icon: <Copy className="h-4 w-4" />,
      label: t("Copiar ID do Post (#{id})", { id: post.id }),
      onClick: () => {
        navigator.clipboard.writeText(post.id.toString());
        showToastSuccess(t("ID copiado com sucesso!"));
      },
      variant: "default",
      category: "edit",
    });

    if (onEdit) {
      actions.push({
        icon: <Edit3 className="h-4 w-4" />,
        label: t("Editar Conteúdo"),
        onClick: onEdit,
        variant: "default",
        category: "edit",
        loading: loadingStates.editing,
      });
    }

    if (onPreview) {
      actions.push({
        icon: <Eye className="h-4 w-4" />,
        label: t("Visualizar"),
        onClick: onPreview,
        variant: "default",
        category: "edit",
      });
    }

    // Categoria: Mídia
    if (
      onUploadImage &&
      (post.status === "pending" || post.status === "approved")
    ) {
      actions.push({
        icon: <ImageIcon className="h-4 w-4" />,
        label: t("Adicionar Imagem"),
        onClick: onUploadImage,
        variant: "default",
        category: "media",
        loading: loadingStates.uploadingImage,
      });
    }

    if (
      onUploadVideo &&
      (post.status === "pending" || post.status === "approved")
    ) {
      actions.push({
        icon: <VideoIcon className="h-4 w-4" />,
        label: t("Adicionar Vídeo"),
        onClick: onUploadVideo,
        variant: "default",
        category: "media",
      });
    }

    // Nota: "Gerar Vídeo com IA" foi removido daqui - está disponível no menu "Otimizar com IA"

    if (
      onEditVideo &&
      post.video_info &&
      (post.video_info.filename || post.video_info.public_url)
    ) {
      actions.push({
        icon: <Edit3 className="h-4 w-4" />,
        label: t("Editar Vídeo"),
        onClick: onEditVideo,
        variant: "default",
        category: "media",
      });
    }

    if (onClearVideo && post.video_info) {
      actions.push({
        icon: <XCircle className="h-4 w-4" />,
        label: t("Remover Vídeo"),
        onClick: onClearVideo,
        variant: "warning",
        category: "media",
      });
    }

    if (onViewVariations && (post.image_info as any)?.variations) {
      actions.push({
        icon: <Maximize2 className="h-4 w-4" />,
        label: t("Ver Variações Sociais"),
        onClick: () =>
          onViewVariations(
            (post.image_info as any).variations,
            (post.image_info as any).public_url || "",
          ),
        variant: "default",
        category: "media",
      });
    }

    if (
      onGenerateCreative &&
      (post.status === "pending" ||
        post.status === "approved" ||
        post.status === "scheduled")
    ) {
      actions.push({
        icon: <Sparkles className="h-4 w-4" />,
        label: t("Criativo com texto"),
        onClick: onGenerateCreative,
        variant: "default",
        category: "media",
      });
    }

    // Categoria: Agendamento
    if (
      onSchedule &&
      (post.status === "approved" || post.status === "scheduled")
    ) {
      actions.push({
        icon: <CalendarPlus className="h-4 w-4" />,
        label: t("Agendar Publicação"),
        onClick: onSchedule,
        variant: "default",
        category: "schedule",
        loading: loadingStates.scheduling,
      });
    }

    // Categoria: Anúncios pagos (Meta Ads) - turbinar post aprovado/publicado
    if (
      onBoostMetaAds &&
      (post.status === "approved" ||
        post.status === "scheduled" ||
        post.status === "published")
    ) {
      actions.push({
        icon: <DollarSign className="h-4 w-4" />,
        label: t("Turbinar com Meta Ads"),
        onClick: onBoostMetaAds,
        variant: "default",
        category: "schedule",
      });
    }

    if (
      onBoostLinkedInAds &&
      (post.status === "approved" ||
        post.status === "scheduled" ||
        post.status === "published")
    ) {
      actions.push({
        icon: <Linkedin className="h-4 w-4" />,
        label: t("Patrocinar no LinkedIn"),
        onClick: onBoostLinkedInAds,
        variant: "default",
        category: "schedule",
      });
    }

    if (
      onSyncMetrics &&
      post.status === "published" &&
      post.platform !== "google_my_business"
    ) {
      actions.push({
        icon: <RefreshCw className="h-4 w-4" />,
        label: t("Sincronizar Métricas"),
        onClick: onSyncMetrics,
        variant: "default",
        category: "schedule",
      });
    }

    // Categoria: Ações de Risco
    if (onReject && post.status === "pending") {
      actions.push({
        icon: <XCircle className="h-4 w-4" />,
        label: t("Descartar"),
        onClick: onReject,
        variant: "destructive",
        category: "danger",
        loading: loadingStates.rejecting,
      });
    }

    // Reverter para pendente (apenas para posts aprovados)
    if (onRevertToPending && post.status === "approved") {
      actions.push({
        icon: <RefreshCw className="h-4 w-4" />,
        label: t("Reverter para Pendente"),
        onClick: onRevertToPending,
        variant: "warning",
        category: "edit",
        loading: loadingStates.rejecting,
      });
    }

    if (onUnschedule && post.status === "scheduled") {
      actions.push({
        icon: <XCircle className="h-4 w-4" />,
        label: t("Remover Agendamento"),
        onClick: onUnschedule,
        variant: "destructive",
        category: "danger",
      });
    }

    if (onDelete && post.status !== "scheduled") {
      actions.push({
        icon: <Trash2 className="h-4 w-4" />,
        label:
          post.status === "published" ? t("Ocultar Post") : t("Excluir Post"),
        onClick: onDelete,
        variant: "destructive",
        category: "danger",
        loading: loadingStates.deleting,
      });
    }

    return actions;
  };

  // Agrupar ações por categoria (apenas gestão, sem IA e publicação)
  const groupActionsByCategory = (actions: MenuAction[]) => {
    const grouped: Record<string, MenuAction[]> = {
      edit: [],
      media: [],
      schedule: [],
      danger: [],
    };

    actions.forEach((action) => {
      if (
        action.category &&
        (action.category === "edit" ||
          action.category === "media" ||
          action.category === "schedule" ||
          action.category === "danger")
      ) {
        grouped[action.category].push(action);
      }
    });

    return grouped;
  };

  const menuActions = getMenuActions();
  const groupedActions = groupActionsByCategory(menuActions);

  // Determinar ações principais (botões destacados)
  const getPrimaryActions = () => {
    const actions: Array<{
      icon: React.ReactNode;
      label: string;
      onClick: () => void;
      variant?: "primary" | "success" | "destructive";
      loading?: boolean;
    }> = [];

    if (onApprove && post.status === "pending") {
      actions.push({
        icon: <CheckCircle className="h-4 w-4" />,
        label: t("Aprovar"),
        onClick: onApprove,
        variant: "success",
        loading: loadingStates.approving,
      });

      if (onSchedule) {
        actions.push({
          icon: <Calendar className="h-4 w-4" />,
          label: t("Aprovar com Horário"),
          onClick: onSchedule,
          variant: "primary",
          loading: loadingStates.scheduling,
        });
      }
    }

    if (
      onPublish &&
      (post.status === "approved" || post.status === "scheduled") &&
      !post.published_at
    ) {
      actions.push({
        icon: <Send className="h-4 w-4" />,
        label: t("Publicar Agora"),
        onClick: onPublish,
        variant: "primary",
        loading: loadingStates.publishing,
      });
    }

    if (
      onPublishMulti &&
      (post.status === "approved" || post.status === "scheduled")
    ) {
      actions.push({
        icon: <Share2 className="h-4 w-4" />,
        label: t("Multi Plataforma"),
        onClick: onPublishMulti,
        variant: "primary",
      });
    }

    if (onRepublish && post.status === "published") {
      actions.push({
        icon: <RefreshCw className="h-4 w-4" />,
        label: t("Republicar"),
        onClick: onRepublish,
        variant: "primary",
        loading: loadingStates.republishing,
      });
    }

    if (onReschedule && post.status === "published") {
      actions.push({
        icon: <CalendarPlus className="h-4 w-4" />,
        label: t("Reagendar"),
        onClick: onReschedule,
        variant: "primary",
      });
    }

    return actions;
  };

  const primaryActions = getPrimaryActions();

  // Labels das categorias (Sem emojis para visual premium minimalista)
  const categoryConfig: Record<
    string,
    {
      label: string;
      color: string;
      bgFrom: string;
      bgTo: string;
      border: string;
    }
  > = {
    edit: {
      label: t("Edição"),
      color: "text-purple-700",
      bgFrom: "from-purple-50",
      bgTo: "to-purple-100",
      border: "border-purple-200",
    },
    media: {
      label: t("Mídia"),
      color: "text-blue-700",
      bgFrom: "from-blue-50",
      bgTo: "to-blue-100",
      border: "border-blue-200",
    },
    schedule: {
      label: t("Agendamento"),
      color: "text-green-700",
      bgFrom: "from-green-50",
      bgTo: "to-green-100",
      border: "border-green-200",
    },
    danger: {
      label: t("Ações de Risco"),
      color: "text-red-700",
      bgFrom: "from-red-50",
      bgTo: "to-red-100",
      border: "border-red-200",
    },
  };

  return (
    <div
      ref={innerRef}
      onClick={handleCardClick}
      onKeyDown={handleCardClick}
      role="button"
      tabIndex={0}
      className={cn(
        "group relative", // Removido overflow-hidden para o menu não cortar
        "border border-purple-200/80 rounded-2xl p-4",
        "bg-white hover:bg-linear-to-br hover:from-white hover:to-purple-50/30",
        "hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-purple-200 transition-all duration-300 transform hover:scale-[1.01]",
        // Sem h-full: no CSS Grid, uma altura explícita de 100% ainda preenche a
        // altura da linha mesmo com items-start no container (ver ResponsiveGrid),
        // esticando cards mais curtos (ex: rejeitados, sem botões) e deixando vão vazio.
        "cursor-pointer flex flex-col",
        "focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-1",
        isMenuOpen ? "z-50" : "z-0", // Garante que o menu aberto fique sobre outros cards
        highlighted &&
          "ring-2 ring-purple-500 bg-linear-to-br from-purple-50 to-white border-purple-400 shadow-lg",
        post.status === "scheduled" && "border-green-300 bg-green-50/10",
      )}
    >
      {/* HEADER: "Profile Header" */}
      <div className="flex items-center justify-between gap-3 pb-3 mb-3 border-b border-gray-100 relative z-20">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Avatar da Plataforma com cor da marca */}
          {post.platform ? (
            (() => {
              const platformConfig = getPlatformConfig(post.platform);
              const IconComponent = platformConfig.icon;
              return (
                <div className="relative shrink-0">
                  <div
                    className={cn(
                      "w-9 h-9 rounded-full flex items-center justify-center",
                      "ring-1 ring-inset ring-black/8 shadow-xs",
                      platformConfig.bgColor,
                    )}
                  >
                    <IconComponent
                      className={cn("h-4 w-4", platformConfig.color)}
                    />
                  </div>
                  {post.agent_generated && (
                    <Tooltip content={t("Gerado por IA")} position="top">
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-purple-600 border border-white flex items-center justify-center shadow-xs z-10">
                        <Bot className="h-[10px] w-[10px] text-white" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="relative shrink-0">
              <div className="w-9 h-9 rounded-full bg-gray-100 ring-1 ring-inset ring-black/8 flex items-center justify-center shadow-xs">
                <div className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          )}

          {/* Textos: Nome da Plataforma e Status Contextual */}
          <div className="flex flex-col min-w-0">
            <span className="text-[13.5px] font-semibold text-gray-900 leading-tight whitespace-nowrap">
              {post.platform ? getPlatformName(post.platform) : t("Postagem")}
            </span>

            <div className="flex items-center gap-1.5 mt-1">
              {(() => {
                if (post.status === "approved" && post.scheduled_date) {
                  return (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse shrink-0" />
                      <span className="text-[11px] text-gray-500 whitespace-nowrap">
                        {t("Agendado: {date}", {
                          date: formatDate(post.scheduled_date),
                        })}
                      </span>
                    </>
                  );
                }

                const statusConfig: Record<
                  string,
                  {
                    label: string;
                    dotClass: string;
                    textClass: string;
                    pulse?: boolean;
                  }
                > = {
                  pending: {
                    label: t("Aguardando aprovação"),
                    dotClass: "bg-amber-400",
                    textClass: "text-amber-600",
                    pulse: true,
                  },
                  approved: {
                    label: t("Aprovado"),
                    dotClass: "bg-emerald-400",
                    textClass: "text-emerald-600",
                  },
                  scheduled: {
                    label: t("Agendado"),
                    dotClass: "bg-blue-400",
                    textClass: "text-blue-600",
                    pulse: true,
                  },
                  published: {
                    label: t("Publicado"),
                    dotClass: "bg-purple-500",
                    textClass: "text-purple-600",
                  },
                  rejected: {
                    label: t("Rejeitado"),
                    dotClass: "bg-red-400",
                    textClass: "text-red-500",
                  },
                  failed: {
                    label: t("Falha ao publicar"),
                    dotClass: "bg-red-500",
                    textClass: "text-red-600",
                  },
                };

                const config = statusConfig[post.status] || {
                  label: post.status,
                  dotClass: "bg-gray-400",
                  textClass: "text-gray-500",
                };

                return (
                  <>
                    <span
                      className={cn(
                        "w-1.5 h-1.5 rounded-full shrink-0",
                        config.dotClass,
                        config.pulse && "animate-pulse",
                      )}
                    />
                    <span
                      className={cn(
                        "text-[11px] font-medium whitespace-nowrap",
                        config.textClass,
                      )}
                    >
                      {config.label}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Controles da direita: ID e Menu */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] font-mono font-medium text-slate-400 bg-slate-50 border border-slate-100 rounded-sm px-1.5 py-0.5 hidden sm:inline-block">
            #{post.id}
          </span>
          {menuActions.length > 0 && (
            <div className="relative">
              <Tooltip content={t("Mais opções")} position="left">
                <button
                  ref={buttonRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  className={cn(
                    "p-1.5 rounded-md transition-all duration-200 flex items-center justify-center",
                    "text-slate-500 bg-slate-50/80 border border-slate-100 hover:bg-slate-100 hover:text-slate-800 hover:border-slate-200",
                    "active:scale-95 outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500",
                    isMenuOpen &&
                      "bg-slate-100 text-slate-800 border-slate-200 shadow-inner",
                  )}
                  aria-label={t("Menu de opções")}
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </Tooltip>

              {isMenuOpen && (
                <div
                  ref={menuRef}
                  className={cn(
                    "absolute right-0 top-full mt-1 w-64 bg-white rounded-lg border border-purple-200 shadow-xl z-100",
                    "animate-fade-in right-0 p-1.5 space-y-0.5",
                  )}
                  style={{
                    top: position.top || "calc(100% + 4px)",
                    bottom: position.bottom,
                    left: position.left,
                    right:
                      position.right !== undefined ? position.right : undefined,
                    transform: position.transform,
                    zIndex: 9999, // Z-index alto para ficar sobre as tags e imagens
                    maxHeight: position.maxHeight || "calc(100vh - 120px)",
                    overflowY: "auto",
                  }}
                  role="menu"
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {/* Renderizar ações agrupadas por categoria */}
                  {Object.entries(groupedActions).map(([category, actions]) => {
                    if (actions.length === 0) return null;

                    return (
                      <React.Fragment key={category}>
                        {/* Label da categoria */}
                        {(() => {
                          const config = categoryConfig[category] || {
                            label: category,
                            color: "text-gray-500",
                          };
                          return (
                            <div
                              className={cn(
                                "flex items-center gap-1.5 px-2 py-1 mt-1 border-t border-purple-50/50 first:mt-0 first:border-0",
                              )}
                            >
                              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                {config.label}
                              </span>
                            </div>
                          );
                        })()}

                        {/* Ações da categoria */}
                        {actions.map((action, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsMenuOpen(false);
                              action.onClick();
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsMenuOpen(false);
                              action.onClick();
                            }}
                            disabled={action.loading}
                            className={cn(
                              "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md group",
                              action.variant === "destructive"
                                ? "text-red-600 hover:bg-red-50"
                                : action.variant === "warning"
                                  ? "text-amber-600 hover:bg-amber-50"
                                  : "text-gray-700 hover:bg-gray-100/80 hover:text-purple-600",
                              action.loading && "opacity-50 cursor-not-allowed",
                            )}
                            role="menuitem"
                          >
                            <span
                              className={cn(
                                action.variant === "destructive"
                                  ? "text-red-500"
                                  : action.variant === "warning"
                                    ? "text-amber-500"
                                    : "text-gray-400 group-hover:text-purple-500",
                                "shrink-0",
                              )}
                            >
                              {action.icon &&
                                React.isValidElement(action.icon) &&
                                React.cloneElement(
                                  action.icon as React.ReactElement<{
                                    className?: string;
                                  }>,
                                  { className: "h-4 w-4" },
                                )}
                            </span>
                            <span className="flex-1 font-medium text-[13px]">
                              {action.label}
                            </span>
                            {action.loading && (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 shrink-0" />
                            )}
                          </button>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo do post */}
      {/* flex-1 só quando há botões de ação abaixo (para empurrá-los pro rodapé);
          sem eles, esticar geraria um vão vazio depois da mídia (ex: posts rejeitados) */}
      <div
        className={cn(
          "flex flex-col relative z-0",
          primaryActions.length > 0 && "flex-1",
        )}
      >
        {post.status === "rejected" &&
          (() => {
            const rejectionText =
              post.rejection_reason ||
              post.failure_reason ||
              t(
                "Motivo não especificado. (Rejeitado antes da atualização do sistema)",
              );
            return (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 mr-2 shrink-0" />
                <div className="text-sm text-red-800 min-w-0 flex-1">
                  <strong className="block mb-1">
                    {t("Motivo da Rejeição/Falha:")}
                  </strong>
                  <p
                    ref={rejectionTextRef}
                    className={cn(
                      "min-h-[40px]",
                      !isRejectionExpanded && "line-clamp-2",
                    )}
                  >
                    {rejectionText}
                    {rejectionIsClamped && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsRejectionExpanded(!isRejectionExpanded);
                        }}
                        className="ml-1 text-red-700 hover:text-red-900 font-bold underline whitespace-nowrap"
                      >
                        {isRejectionExpanded ? t("Ver menos") : t("Ver mais")}
                      </button>
                    )}
                  </p>
                </div>
              </div>
            );
          })()}
        {/* Conteúdo do Post */}
        <div className="mb-2 flex-none px-1 min-h-[64px]">
          {post.content && (
            <>
              <p
                className={cn(
                  "text-sm text-gray-700 leading-relaxed whitespace-pre-wrap wrap-break-word font-medium",
                  !isExpanded && "line-clamp-3",
                )}
              >
                {shouldTruncate && !isExpanded
                  ? previewContent.substring(0, maxContentLength).trim() +
                    "... "
                  : post.content + " "}
                {shouldTruncate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    className="text-xs text-purple-600 hover:text-purple-800 font-bold transition-colors whitespace-nowrap"
                  >
                    {isExpanded ? t("Ver menos") : t("Ver mais")}
                  </button>
                )}
              </p>
            </>
          )}
          {post.status === "failed" && post.failure_reason && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 animate-pulse">
              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-red-700 uppercase leading-none mb-1">
                  {t("Erro na Publicação")}
                </span>
                <p className="text-[11px] text-red-600 font-medium leading-tight">
                  {post.failure_reason}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Gatilho emocional usado no hook (Creative AI) */}
        {post.emotional_trigger &&
          EMOTIONAL_TRIGGER_LABELS[post.emotional_trigger] && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-orange-600 bg-orange-50 text-[10px] font-medium border border-orange-100">
                <Flame className="h-2.5 w-2.5" />
                {EMOTIONAL_TRIGGER_LABELS[post.emotional_trigger]}
              </span>
            </div>
          )}

        {/* Hashtags */}
        {(() => {
          // Alguns posts antigos têm hashtags salvo como a string literal "[]"
          // (em vez de um array vazio de verdade) — filtra esse tipo de lixo
          // pra não renderizar uma tag "#[]".
          const isGarbageTag = (tag: string) =>
            !tag || tag === "[]" || tag === "null" || tag === "undefined";

          // Posts antigos (bug corrigido no backend) foram salvos com hashtags
          // serializadas como literal de array do Postgres ("{tag1,tag2}"),
          // então limpa chaves/# residuais de cada tag antes de exibir (o "#"
          // já é adicionado no JSX abaixo, não deveria vir embutido na tag).
          const cleanTag = (tag: string) =>
            tag
              .trim()
              .replace(/^[{]+|[}]+$/g, "")
              .replace(/^#+/, "")
              .trim();

          let hashtagsArray: string[] = [];
          if (Array.isArray(post.hashtags)) {
            hashtagsArray = (post.hashtags as string[])
              .map(cleanTag)
              .filter((tag) => !isGarbageTag(tag));
          } else if (
            post.hashtags &&
            typeof post.hashtags === "string" &&
            (post.hashtags as string).trim()
          ) {
            hashtagsArray = (post.hashtags as string)
              .split(",")
              .map(cleanTag)
              .filter((tag: string) => tag.length > 0 && !isGarbageTag(tag));
          }

          if (hashtagsArray.length > 0) {
            return (
              <div className="mb-2 flex flex-wrap gap-1.5 z-0 relative min-h-[50px] content-start">
                {hashtagsArray.slice(0, 4).map((tag: string, index: number) => (
                  <span
                    key={index}
                    className="px-1.5 py-0.5 rounded-sm text-purple-600 bg-purple-50 text-[10px] font-medium border border-purple-100 max-w-full truncate"
                  >
                    #{tag}
                  </span>
                ))}
                {hashtagsArray.length > 4 && (
                  <span className="text-[10px] text-gray-500 self-center font-medium bg-gray-100 px-1.5 py-0.5 rounded-md">
                    +{hashtagsArray.length - 4}
                  </span>
                )}
              </div>
            );
          }
          return <div className="mb-2 min-h-[50px]"></div>;
        })()}

        {/* Call to Action */}
        <div className="mb-1.5 min-h-[48px]">
          {post.call_to_action &&
            (post.call_to_action_url ? (
              <a
                href={post.call_to_action_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200 hover:bg-blue-100"
              >
                <span className="line-clamp-2 text-left">
                  {post.call_to_action}
                </span>
              </a>
            ) : (
              <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200">
                <span className="line-clamp-2 text-left">
                  {post.call_to_action}
                </span>
              </div>
            ))}
        </div>

        {/* Imagem Principal / Galeria */}
        {((post.image_info &&
          (post.image_info.filename || post.image_info.public_url)) ||
          (post.media_gallery && post.media_gallery.length > 0)) && (
          <div className="group/image mb-2 rounded-md overflow-hidden border border-purple-200 bg-gray-50 relative h-36">
            {(post.image_info as any)?.variations && onViewVariations && (
              <div className="absolute bottom-2 left-2 z-20">
                <Tooltip
                  content={t(
                    "IA redimensionou para todos formatos sociais (Clique para ver)",
                  )}
                  position="right"
                >
                  <Badge
                    variant="purple"
                    size="sm"
                    className="cursor-pointer hover:scale-105 transition-transform bg-linear-to-r from-purple-500 to-indigo-500 text-white border-none shadow-md backdrop-blur-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewVariations(
                        (post.image_info as any).variations,
                        (post.image_info as any).public_url || "",
                      );
                    }}
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    <span className="text-[9px] font-bold uppercase tracking-wider">
                      {t("Smart Resized")}
                    </span>
                  </Badge>
                </Tooltip>
              </div>
            )}
            {!imageError ? (
              <>
                {/* Se houver galeria, mostrar carrossel interativo */}
                {post.media_gallery && post.media_gallery.length > 0 ? (
                  <div className="relative h-full w-full flex items-center justify-center bg-gray-100 group/carousel">
                    {post.media_gallery[currentMediaIndex].type === "video" ? (
                      post.media_gallery[currentMediaIndex].public_url ||
                      post.media_gallery[currentMediaIndex].url ? (
                        <video
                          src={
                            post.media_gallery[currentMediaIndex].public_url ||
                            post.media_gallery[currentMediaIndex].url
                          }
                          className="w-full h-full object-cover"
                          controls
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center">
                          <VideoIcon className="h-10 w-10 text-purple-400 opacity-50" />
                          <span className="text-[10px] text-gray-400 mt-1">
                            {t("Vídeo na galeria")}
                          </span>
                        </div>
                      )
                    ) : (
                      <img
                        src={
                          post.media_gallery[currentMediaIndex].public_url ||
                          post.media_gallery[currentMediaIndex].url ||
                          ""
                        }
                        alt={t("Imagem {index} do post {id}", {
                          index: currentMediaIndex + 1,
                          id: post.id,
                        })}
                        className="w-full h-full object-cover animate-fade-in"
                        onError={() => setImageError(true)}
                      />
                    )}

                    {/* Navegação do Carrossel */}
                    {post.media_gallery.length > 1 && (
                      <>
                        <div className="absolute top-2 left-2 px-2 py-1 rounded-lg bg-black/60 text-white text-[10px] font-bold backdrop-blur-xs shadow-xs flex items-center gap-1.5 z-10 border border-white/10">
                          <ImageIcon className="h-3 w-3" />
                          <span>
                            {currentMediaIndex + 1} /{" "}
                            {post.media_gallery.length}
                          </span>
                        </div>

                        {/* Botões Prev/Next (Visíveis no hover ou mobile) */}
                        <button
                          onClick={prevMedia}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 text-purple-600 border border-purple-100 opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-white hover:scale-110 shadow-md z-20"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2.5"
                              d="M15 19l-7-7 7-7"
                            ></path>
                          </svg>
                        </button>

                        <button
                          onClick={nextMedia}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 text-purple-600 border border-purple-100 opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-white hover:scale-110 shadow-md z-20"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2.5"
                              d="M9 5l7 7-7 7"
                            ></path>
                          </svg>
                        </button>

                        {/* Dots indicadores */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                          {post.media_gallery.map((_, i) => (
                            <div
                              key={i}
                              className={cn(
                                "w-1.5 h-1.5 rounded-full transition-all duration-300",
                                i === currentMediaIndex
                                  ? "bg-purple-500 w-3"
                                  : "bg-white/60",
                              )}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <img
                    src={
                      post.image_info?.public_url ||
                      buildApiUrl(`/api/images/${post.image_info?.filename}`)
                    }
                    alt={t("Imagem do post {id}", { id: post.id })}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover/image:scale-110"
                    onError={() => setImageError(true)}
                  />
                )}

                <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/10 transition-colors duration-300 pointer-events-none" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsImageFullscreen(true);
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 backdrop-blur-xs border border-purple-200 text-purple-600 opacity-0 group-hover/image:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 hover:shadow-md z-10"
                  title={t("Ver imagem em tela cheia")}
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-36 bg-gray-50">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mb-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                </div>
                <p className="text-[10px] font-medium text-gray-700 mb-2">
                  {t("Imagem não disponível")}
                </p>
                <div className="flex gap-1.5 justify-center items-center flex-nowrap">
                  {onUploadImage && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onUploadImage();
                      }}
                      variant="secondary"
                      size="sm"
                      icon={<ImageIcon className="h-3 w-3" />}
                      iconPosition="left"
                      className="text-[10px] px-2 py-1 h-6 whitespace-nowrap flex-nowrap border border-purple-200"
                    >
                      {t("Novo Upload")}
                    </Button>
                  )}
                  {onClearImage && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearImage();
                      }}
                      variant="ghost"
                      size="sm"
                      icon={<XCircle className="h-3 w-3" />}
                      iconPosition="left"
                      className="text-[10px] px-2 py-1 h-6 whitespace-nowrap flex-nowrap border border-purple-200"
                    >
                      {t("Remover")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vídeo */}
        {post.video_info &&
          (post.video_info.filename || post.video_info.public_url) &&
          (!post.media_gallery || post.media_gallery.length === 0) &&
          (() => {
            let videoSrc = "";
            if (post.video_info.public_url) {
              const publicUrl = post.video_info.public_url;
              if (
                publicUrl.startsWith("http://127.0.0.1:") ||
                publicUrl.startsWith("http://localhost:")
              ) {
                try {
                  const url = new URL(publicUrl);
                  videoSrc = url.pathname;
                } catch {
                  videoSrc = publicUrl;
                }
              } else if (
                publicUrl.startsWith("http://") ||
                publicUrl.startsWith("https://")
              ) {
                videoSrc = publicUrl;
              } else if (publicUrl.startsWith("/")) {
                videoSrc = publicUrl;
              } else {
                videoSrc = buildApiUrl(
                  `/api/videos/${post.video_info.filename || publicUrl}`,
                );
              }
            } else if (post.video_info.filename) {
              videoSrc = buildApiUrl(`/api/videos/${post.video_info.filename}`);
            }

            return (
              <div className="mb-2 rounded-md overflow-hidden border border-purple-200 bg-gray-50 relative h-36">
                {!videoError ? (
                  <video
                    src={videoSrc}
                    controls
                    className="w-full h-full object-cover bg-black"
                    preload="metadata"
                    crossOrigin="anonymous"
                    onError={() => setVideoError(true)}
                  >
                    <track kind="captions" />
                    {t("Seu navegador não suporta a tag de vídeo.")}
                  </video>
                ) : (
                  <div className="flex flex-col items-center justify-center h-36 bg-gray-50">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mb-1.5">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <p className="text-[10px] font-medium text-gray-700 mb-2">
                      {t("Vídeo não disponível")}
                    </p>
                    <div className="flex gap-1.5 justify-center items-center flex-nowrap">
                      {onUploadVideo && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUploadVideo();
                          }}
                          variant="secondary"
                          size="sm"
                          icon={<VideoIcon className="h-3 w-3" />}
                          iconPosition="left"
                          className="text-[10px] px-2 py-1 h-6 whitespace-nowrap flex-nowrap border border-purple-200"
                        >
                          {t("Novo Upload")}
                        </Button>
                      )}
                      {onClearVideo && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            onClearVideo();
                          }}
                          variant="ghost"
                          size="sm"
                          icon={<XCircle className="h-3 w-3" />}
                          iconPosition="left"
                          className="text-[10px] px-2 py-1 h-6 whitespace-nowrap flex-nowrap border border-purple-200"
                        >
                          {t("Remover")}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        {/* Placeholder quando não há imagem nem vídeo nem galeria */}
        {(!post.image_info ||
          (!post.image_info.filename && !post.image_info.public_url)) &&
          (!post.video_info ||
            (!post.video_info.filename && !post.video_info.public_url)) &&
          (!post.media_gallery || post.media_gallery.length === 0) && (
            <div className="mb-2 rounded-md border-2 border-dashed border-purple-300 bg-gray-50/50 relative h-36 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                    <ImageIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                    <VideoIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <p className="text-[10px] font-medium text-gray-600 mb-0.5">
                  {t("Sem mídia")}
                </p>
                <p className="text-[10px] text-gray-400 text-center">
                  {t("Este post não possui imagem ou vídeo")}
                </p>
              </div>
            </div>
          )}

        {/* Data de publicação, métricas e Link externo - Compacto */}
        {(post.published_at ||
          (post.status === "scheduled" && post.scheduled_date) ||
          post.linkedin_url ||
          post.facebook_url ||
          post.instagram_url ||
          post.utm_url) && (
          <div className="flex flex-col gap-1 mt-1 justify-start w-full">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {post.published_at && (
                <div
                  className="text-[10px] text-gray-500 flex items-center gap-1"
                  title={t("Data de publicação")}
                >
                  <CheckCircle className="h-3 w-3 text-green-500" />
                  <span>
                    {t("Publicado: {date}", {
                      date: formatDate(post.published_at),
                    })}
                  </span>
                </div>
              )}
              {post.status === "scheduled" && post.scheduled_date && (
                <div
                  className="text-[10px] text-gray-600 font-bold flex items-center gap-1"
                  title={t("Agendado para")}
                >
                  <CalendarPlus className="h-3.5 w-3.5 text-purple-600" />
                  <span>
                    {t("Agendado: {date}", {
                      date: formatDate(post.scheduled_date),
                    })}
                  </span>
                </div>
              )}
              {post.linkedin_url && (
                <a
                  href={post.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] text-purple-600 hover:text-purple-700 transition-colors font-medium group/link whitespace-nowrap shrink-0 ml-auto"
                >
                  <ExternalLink className="h-3 w-3 group-hover/link:translate-x-0.5 transition-transform" />
                  <span>{t("Ver no LinkedIn")}</span>
                </a>
              )}
              {post.facebook_url && (
                <a
                  href={post.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] text-purple-600 hover:text-purple-700 transition-colors font-medium group/link whitespace-nowrap shrink-0 ml-auto"
                >
                  <ExternalLink className="h-3 w-3 group-hover/link:translate-x-0.5 transition-transform" />
                  <span>{t("Ver no Facebook")}</span>
                </a>
              )}
              {post.instagram_url && (
                <a
                  href={post.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[10px] text-purple-600 hover:text-purple-700 transition-colors font-medium group/link whitespace-nowrap shrink-0 ml-auto"
                >
                  <ExternalLink className="h-3 w-3 group-hover/link:translate-x-0.5 transition-transform" />
                  <span>{t("Ver no Instagram")}</span>
                </a>
              )}
              {post.published_at && (
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 w-full">
                  <div
                    className="text-[10px] text-gray-500 font-bold flex items-center gap-1"
                    title={t("Alcance / Impressões")}
                  >
                    <Eye className="h-3.5 w-3.5 text-blue-500" />
                    <span>
                      {postMetrics?.impressions || postMetrics?.reach || 0}
                    </span>
                  </div>
                  <div
                    className="text-[10px] text-gray-500 font-bold flex items-center gap-1"
                    title={t("Cliques")}
                  >
                    <MousePointer2 className="h-3.5 w-3.5 text-orange-500" />
                    <span>{postMetrics?.clicks || 0}</span>
                  </div>
                  <div
                    className="text-[10px] text-gray-500 font-bold flex items-center gap-1"
                    title={t("Curtidas")}
                  >
                    <Heart className="h-3.5 w-3.5 text-red-500" />
                    <span>{postMetrics?.likes || 0}</span>
                  </div>
                  <div
                    className="text-[10px] text-gray-500 font-bold flex items-center gap-1"
                    title={t("Comentários")}
                  >
                    <MessageCircle className="h-3.5 w-3.5 text-green-500" />
                    <span>{postMetrics?.comments || 0}</span>
                  </div>
                  <div
                    className="text-[10px] text-gray-500 font-bold flex items-center gap-1"
                    title={t("Compartilhamentos")}
                  >
                    <Share2 className="h-3.5 w-3.5 text-purple-500" />
                    <span>{postMetrics?.shares || 0}</span>
                  </div>
                  <div
                    className="text-[10px] text-gray-500 font-bold flex items-center gap-1"
                    title={t("Taxa de Engajamento")}
                  >
                    <Activity className="h-3.5 w-3.5 text-indigo-500" />
                    <span>{postMetrics?.engagement_rate || 0}%</span>
                  </div>
                </div>
              )}
            </div>
            {post.utm_url && (
              <div
                className="flex items-center gap-1.5 p-1.5 rounded-lg bg-emerald-50 border border-emerald-100"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-[9px] font-black text-emerald-700 uppercase tracking-wider shrink-0">
                  UTM
                </span>
                <span className="text-[9px] text-emerald-600 truncate flex-1 min-w-0 font-mono">
                  {post.utm_url.replace(/^https?:\/\//, "").slice(0, 50)}…
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(post.utm_url!);
                    setUtmCopied(true);
                    setTimeout(() => setUtmCopied(false), 2000);
                  }}
                  className="shrink-0 p-0.5 rounded-sm text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 transition-colors"
                  title={t("Copiar link UTM")}
                >
                  {utmCopied ? (
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Ações primárias (Botões) colocadas ABAIXO da imagem/mídia e metadados */}
        {primaryActions.length > 0 &&
          (() => {
            // Proporção de largura calculada a partir do tamanho real de cada
            // label — evita que o botão com texto mais longo trunque/quebre,
            // sem deixar o mais curto artificialmente grande. Funciona para
            // qualquer par de ações (Aprovar/Agendar, Publicar/Multi, etc).
            const label0Len = primaryActions[0]?.label.length || 1;
            const label1Len = primaryActions[1]?.label.length || 1;
            const totalLen = label0Len + label1Len;
            const flexGrow0 = Math.max(0.7, (label0Len / totalLen) * 2);
            const flexGrow1 = Math.max(0.7, (label1Len / totalLen) * 2);

            return (
              <div className="flex flex-nowrap items-stretch gap-2 mt-1.5 z-10 relative w-full">
                {/* CTA Primário - Aprovar / Publicar Agora */}
                {primaryActions[0] && (
                  <Button
                    size="sm"
                    variant={primaryActions[0].variant || "success"}
                    icon={primaryActions[0].icon}
                    onClick={(e) => {
                      e.stopPropagation();
                      primaryActions[0].onClick();
                    }}
                    isLoading={primaryActions[0].loading}
                    disabled={primaryActions[0].loading}
                    style={{
                      flexGrow: flexGrow0,
                      backgroundColor:
                        primaryActions[0].variant === "success"
                          ? "#16a34a"
                          : primaryActions[0].variant === "primary"
                            ? "#9333ea"
                            : undefined,
                      backgroundImage:
                        primaryActions[0].variant === "primary"
                          ? "linear-gradient(to right, #9333ea, #4f46e5)"
                          : undefined,
                      color: "#ffffff",
                    }}
                    className="font-bold shrink min-w-0 text-[11px] xl:text-xs shadow-md hover:shadow-lg transition-all rounded-xl hover:scale-[1.02] justify-center py-2 px-3 text-center"
                  >
                    <span className="text-white whitespace-nowrap">
                      {primaryActions[0].label}
                    </span>
                  </Button>
                )}
                {/* Multi Plataforma - segundo botão quando for post aprovado/agendado */}
                {primaryActions.length > 1 && primaryActions[1] && (
                  <Button
                    size="sm"
                    variant={primaryActions[1].variant || "primary"}
                    icon={primaryActions[1].icon}
                    onClick={(e) => {
                      e.stopPropagation();
                      primaryActions[1].onClick();
                    }}
                    isLoading={primaryActions[1].loading}
                    disabled={primaryActions[1].loading}
                    style={{
                      flexGrow: flexGrow1,
                      background: "linear-gradient(to right, #9333ea, #4f46e5)",
                      color: "#ffffff",
                    }}
                    className="font-bold shrink min-w-0 text-[11px] xl:text-xs shadow-md hover:shadow-lg transition-all rounded-xl hover:scale-[1.02] justify-center py-2 px-2 text-center"
                  >
                    <span className="text-white whitespace-nowrap">
                      {primaryActions[1].label}
                    </span>
                  </Button>
                )}
              </div>
            );
          })()}
      </div>

      {/* Ações secundárias: Otimizar com IA e outras ações */}
      {/* children pode chegar como array de expressões condicionais (ex: [false, false])
          quando o pai passa múltiplos blocos JSX irmãos — um array é sempre truthy em JS,
          mesmo vazio de conteúdo real. React.Children.toArray filtra null/undefined/boolean,
          então isso reflete se existe algo de fato renderizável. */}
      {(React.Children.toArray(children).length > 0 ||
        post.status === "published") && (
        <div
          className="mt-1.5 min-h-[32px] w-full"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          {children}
        </div>
      )}

      {isImageFullscreen && (
        <Modal
          open={isImageFullscreen}
          onClose={() => setIsImageFullscreen(false)}
          title={
            post.media_gallery && post.media_gallery.length > 1
              ? t("Imagem {current} de {total}", {
                  current: currentMediaIndex + 1,
                  total: post.media_gallery.length,
                })
              : t("Visualização da Imagem")
          }
          subtitle={t("Post #{id}", { id: post.id })}
          maxWidth="max-w-4xl"
          headerVariant="gradient"
        >
          <div className="flex items-center justify-center bg-gray-900/5 rounded-2xl overflow-hidden p-2 min-h-[50vh] relative group/modal-carousel">
            {post.media_gallery && post.media_gallery.length > 0 ? (
              <>
                {post.media_gallery[currentMediaIndex].type === "video" ? (
                  <video
                    src={
                      post.media_gallery[currentMediaIndex].public_url ||
                      post.media_gallery[currentMediaIndex].url
                    }
                    controls
                    className="max-w-full max-h-[75vh] shadow-2xl rounded-xl"
                  />
                ) : (
                  <img
                    src={
                      post.media_gallery[currentMediaIndex].public_url ||
                      post.media_gallery[currentMediaIndex].url ||
                      ""
                    }
                    alt={t("Imagem do post {id}", { id: post.id })}
                    className="max-w-full max-h-[75vh] object-contain shadow-2xl rounded-xl animate-modal-scale-in"
                  />
                )}

                {post.media_gallery.length > 1 && (
                  <>
                    <button
                      onClick={prevMedia}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 text-purple-600 shadow-xl border border-purple-100 hover:bg-white hover:scale-110 transition-all z-30"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M15 19l-7-7 7-7"
                        ></path>
                      </svg>
                    </button>

                    <button
                      onClick={nextMedia}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 text-purple-600 shadow-xl border border-purple-100 hover:bg-white hover:scale-110 transition-all z-30"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M9 5l7 7-7 7"
                        ></path>
                      </svg>
                    </button>
                  </>
                )}
              </>
            ) : (
              <img
                src={
                  post.image_info?.public_url ||
                  (post.image_info?.filename
                    ? buildApiUrl(`/api/images/${post.image_info.filename}`)
                    : "")
                }
                alt={t("Imagem do post {id}", { id: post.id })}
                className="max-w-full max-h-[75vh] object-contain shadow-2xl rounded-xl animate-modal-scale-in"
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PostCard;
